import Fuse from 'fuse.js';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  isClinicianFilterApplicableForScope,
  selectedClinicianIdsFilterAtom,
  sessionListScopeAtom,
} from '../../../../../../data/session-list/atoms';
import { useSessionListHost } from '../../../../../session-list/session-list-host-context';

/** cmdk returns 1 for a match and 0 for a miss. */
const FILTER_RESULTS = { MATCH: 1, NO_MATCH: 0 } as const;

interface ClinicianOption {
  kindeUserId: string;
  name: string;
}

const idsDiffer = (prev: string[], current: string[]): boolean => {
  if (prev.length !== current.length) return true;
  const prevSet = new Set(prev);
  return current.some((id) => !prevSet.has(id));
};

// `emitAnalytics` lets a surface opt out of this hook's `sessions_filter_applied`
// emission. The new session list passes `false` and tracks via the shared
// `useSessionsFilterTracking` chokepoint instead; the old sidebar list uses the
// default and keeps emitting on popover close.
export const useClinicianFilter = ({
  emitAnalytics = true,
}: { emitAnalytics?: boolean } = {}) => {
  const host = useSessionListHost();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedClinicianIds, setSelectedClinicianIds] = useAtom(
    selectedClinicianIdsFilterAtom
  );
  const scope = useAtomValue(sessionListScopeAtom);

  const isClinicianFilterApplicable = isClinicianFilterApplicableForScope(scope);

  // The session list only ever shows the current team's sessions, so the only
  // clinicians who can appear are current-team members. `useTeamClinicianList`
  // reads the already-cached, ungated team query (no extra API call, no tier
  // gate), drops support-only seats, and includes the current user only when
  // they hold SESSION_CREATE.
  const {
    clinicians: teamClinicians,
    currentUserId,
    isLoading,
  } = host.identity.useTeamClinicianList();

  const intl = useIntl();
  const meLabel = intl.formatMessage({ defaultMessage: 'Me', id: 'TdbPNK7Ach' });

  const unsortedClinicians: ClinicianOption[] = useMemo(() => {
    if (!isClinicianFilterApplicable) return [];
    return teamClinicians.map((member) => ({
      kindeUserId: member.kinde_user_id,
      name: member.kinde_user_id === currentUserId ? meLabel : member.name,
    }));
  }, [isClinicianFilterApplicable, teamClinicians, currentUserId, meLabel]);

  const selectedClinicianIdsSet = useMemo(
    () => new Set(selectedClinicianIds),
    [selectedClinicianIds]
  );

  const [searchValue, setSearchValue] = useState('');

  // Snapshot of selected ids at the moment the dropdown opens, used to pin
  // selected rows to the top while it stays open. Updated only on open so
  // toggling a checkbox doesn't shuffle the list under the user.
  const [pinnedSelectedIds, setPinnedSelectedIds] = useState<Set<string>>(
    selectedClinicianIdsSet
  );
  useEffect(() => {
    if (isOpen) {
      setPinnedSelectedIds(selectedClinicianIdsSet);
    } else {
      setSearchValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const clinicians = useMemo(() => {
    return [...unsortedClinicians].sort((a, b) => {
      // Self ("Me") pins above everything, including selected rows.
      const aSelf = a.kindeUserId === currentUserId;
      const bSelf = b.kindeUserId === currentUserId;
      if (aSelf && !bSelf) return -1;
      if (!aSelf && bSelf) return 1;
      const aSel = pinnedSelectedIds.has(a.kindeUserId);
      const bSel = pinnedSelectedIds.has(b.kindeUserId);
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      return 0;
    });
  }, [unsortedClinicians, pinnedSelectedIds, currentUserId]);

  const fuse = useMemo(
    () => new Fuse(clinicians, { keys: ['name'], threshold: 0.4 }),
    [clinicians]
  );

  // cmdk calls `handleFilter` once per row per keystroke. Compute the match
  // set once per (fuse, searchValue) change so each row is an O(1) Set lookup.
  const matchSet = useMemo(() => {
    if (!searchValue) return null;
    return new Set(fuse.search(searchValue).map((r) => r.item.kindeUserId));
  }, [fuse, searchValue]);

  const handleFilter = useCallback(
    (value: string) => {
      if (matchSet === null) return FILTER_RESULTS.MATCH;
      return matchSet.has(value) ? FILTER_RESULTS.MATCH : FILTER_RESULTS.NO_MATCH;
    },
    [matchSet]
  );

  const handleSelect = useCallback(
    (kindeUserId: string) => {
      setSelectedClinicianIds((prev) =>
        prev.includes(kindeUserId)
          ? prev.filter((id) => id !== kindeUserId)
          : [...prev, kindeUserId]
      );
    },
    [setSelectedClinicianIds]
  );

  const idsOnOpenRef = useRef<string[]>([]);
  const hasTrackedRef = useRef(false);
  const selectedIdsRef = useRef(selectedClinicianIds);
  selectedIdsRef.current = selectedClinicianIds;
  const handleSetIsOpen = useCallback(
    (open: boolean) => {
      if (!emitAnalytics) {
        setIsOpen(open);
        return;
      }
      if (open) {
        idsOnOpenRef.current = selectedIdsRef.current;
        hasTrackedRef.current = false;
      } else if (!hasTrackedRef.current) {
        if (idsDiffer(idsOnOpenRef.current, selectedIdsRef.current)) {
          host.analytics.track({
            options: { amplitude: false },
            event_name: host.analytics.TrackingEventName.SessionsFilterApplied,
            props: { filter_type: 'clinician' },
          });
          hasTrackedRef.current = true;
        }
      }
      setIsOpen(open);
    },
    [emitAnalytics, host]
  );

  return {
    isOpen,
    setIsOpen: handleSetIsOpen,
    clinicians,
    selectedClinicianIds,
    selectedClinicianIdsSet,
    searchValue,
    setSearchValue,
    handleFilter,
    handleSelect,
    isClinicianFilterApplicable,
    isLoading,
  };
};
